import type { IMenuItem, IModuleStrings, IRecordTypeDefinition, IStatBucket } from '@platform/core/types';
import { customMenuHeadOf } from './customMenuHeads';
import { facetEntryOf } from './facetEntry';
import { libraryCategoryCount, libraryItemMode } from './libraryModes';
import type { IMenuEntry } from './IMenuEntry';
import type { IMenuSection } from './IMenuSection';
import { isWarningItem } from './menuWarning';
import { englishMenuName, menuTypeName, RECORDS } from './menuTypeName';

export const DEFAULT_MENU_ITEM = 'all';

/**
 * Розгортає меню типу запису як прототип v4 (MENU_DEFS · facetItems): групи пунктів мовою інтерфейсу (резолвер рядків модуля: пакет → підпис
 * визначення); групи «за статусом» не рендеряться (етапи — вкладка «Етапи»), група «за полем» стає одним пунктом «За …» у групі пункту таблиці
 * (facetEntryOf), пункти з назвою статусу пропускаються. `{name}` — назва типу: англійською — за правилом прототипу (englishMenuName; головний
 * тип — назва модуля: «All CRM», «All document center»), українською у власному меню — menuTypeName («Усі завдання»), у решті модулів
 * «Усі {name}» — «Усі записи», як у прототипі.
 * Ключі пунктів унікальні в меню: фільтр — `filter:<група>.<пункт>`, пункт із ключем, що вже трапився вище, пропускається; група без пунктів —
 * теж. Ключ розділу — англійська назва групи (не залежить від мови). Чиста функція.
 */
export function buildMenu(rt: IRecordTypeDefinition, facets: Record<string, IStatBucket[]>, strings: IModuleStrings, moduleName: string): IMenuSection[] {
  const sections: IMenuSection[] = [];
  const seen = new Set<string>();
  const own = Boolean(customMenuHeadOf(strings.moduleKey));
  const typeName = strings.entity(rt.key, 'name', rt.name);
  const name = strings.lang !== 'uk' ? englishMenuName(rt.main ? moduleName : typeName)
    : own ? menuTypeName(typeName, moduleName, strings.lang, rt.name.en) : RECORDS.uk;
  const statuses = new Set(rt.statuses.list.map((s) => s.value.toLowerCase()));
  const facetEntries: IMenuEntry[] = [];
  const add = (key: string, title: string, entries: IMenuEntry[]) => {
    const fresh = entries.filter((e) => !seen.has(e.key));
    fresh.forEach((e) => seen.add(e.key));
    if (fresh.length) sections.push({ key, title, entries: fresh });
  };
  rt.menu.forEach((g, gi) => {
    const title = strings.menuGroup(rt.key, g.group.en ?? '', g.group);
    if (g.kind === 'byStatus') return;
    if (g.kind === 'byField') {
      const facet = g.field ? facetEntryOf(rt, g.field, facets[g.field], title) : null;
      if (facet) facetEntries.push(facet);
      return;
    }
    const items = (g.items ?? []).map((it, i) => ({ it, i })).filter(({ it }) => own || !statuses.has((it.label.en ?? '').replace('{name}', '').trim().toLowerCase()));
    add(g.group.en || String(gi), title, items.map(({ it, i }) => itemEntry(rt, it, `${gi}.${i}`, title, name, strings)));
  });
  if (!seen.has(DEFAULT_MENU_ITEM) && !seen.has('view:table')) {
    const label = `${strings.lang === 'uk' ? 'Усі' : 'All'} ${name}`;
    sections.unshift({ key: DEFAULT_MENU_ITEM, title: typeName, entries: [{ key: DEFAULT_MENU_ITEM, label, group: typeName }] });
  }
  const host = Math.max(0, sections.findIndex((s) => s.entries.some((e) => e.key === 'view:table' || e.key === DEFAULT_MENU_ITEM)));
  const target = sections[host];
  if (target) target.entries.push(...facetEntries.filter((e) => !seen.has(e.key)).map((e) => ({ ...e, group: target.title })));
  return sections;
}

/**
 * Пункт меню з пункту метаданих. Пункт, що відкриває подання-бібліотеку, отримує режим (`mode` пункту, якщо метадані його мають, інакше
 * «Усі категорії») і лічильник категорій, як «Усі категорії → 6» прототипу.
 */
function itemEntry(rt: IRecordTypeDefinition, it: IMenuItem, position: string, title: string, name: string, strings: IModuleStrings): IMenuEntry {
  const mode = libraryItemMode(rt, it.view, 'mode' in it && typeof it.mode === 'string' ? it.mode : undefined);
  return {
    key: it.view ? `view:${it.view}${mode && mode !== 'categories' ? `:${mode}` : ''}` : it.preset ? `preset:${it.preset}` : it.filter ? `filter:${position}` : DEFAULT_MENU_ITEM,
    label: strings.menuItem(rt.key, it.label.en ?? '', it.label).replace('{name}', name), group: title,
    view: it.view, preset: it.preset, filter: it.filter, warning: isWarningItem(rt.key, it),
    ...(mode ? { mode, count: mode === 'categories' && it.view ? libraryCategoryCount(rt, it.view) : undefined } : {}),
  };
}

/**
 * Пункт меню за ключем: серед `sections`, далі серед `fallback` (меню без лічильників полів — пункт «За …», записів зі значенням якого зараз
 * немає, лишається тим самим пунктом), далі пункт поза меню `adHoc` (пресет із поради: adHocEntryOf), інакше перший пункт меню (типовий «Усі»).
 */
export function findEntry(sections: IMenuSection[], key: string | undefined, fallback: IMenuSection[] = [], adHoc?: IMenuEntry): IMenuEntry {
  for (const list of [sections, fallback]) for (const s of list) for (const e of s.entries) if (e.key === key) return e;
  return adHoc ?? sections[0]?.entries[0] ?? { key: DEFAULT_MENU_ITEM, label: '', group: '' };
}
